import * as STD                       from '@emersya/mpa-std';
import * as CM                        from '@emersya/mpa-components-manager';

export interface IInputSelectComponentConfiguration extends IInputComponentConfiguration<
	CM.Component<any, any>,
	CM.Component.TId
> {
	/**
	 * Id of a standard rendering mode or path to an instance of InputSelectRenderingMode
	 * Default = 'dropdown'
	 */
	renderingMode?: (
		'radio' |
		'radio-stylized' |
		'dropdown' |
		CM.PropertyOperation.TDescriptor
		);
};
/**
 * Interface designed for allowing to have only one input-select class
 * that can have many different representation such as radio, radio-stylized or dropdown.
 * The rendering mode is responsible for DOM generation / events dispatching etc...
 **/
export type TInputSelectRenderingMode<
	TComponent extends InputSelectComponent<TComponent, TConfiguration>,
	TConfiguration extends IInputSelectComponentConfiguration = IInputSelectComponentConfiguration
> = CM.Plugin.TPlugin<
	InputSelectComponent<TComponent, TConfiguration>,
	{
		getSlotDOM(component: InputSelectComponent<TComponent, TConfiguration>): HTMLElement;
	}
>;

export class InputSelectComponent<
	TComponent extends InputSelectComponent<TComponent, TConfiguration>,
	TConfiguration extends IInputSelectComponentConfiguration = IInputSelectComponentConfiguration
> extends InputComponent<
	HTMLDivElement,
	CM.Component<any, any>,
	CM.Component.TId,
	TComponent,
	TConfiguration
> {
	static readonly standardRenderingModes: {
		dropdown: DropdownRenderingMode;
		radio: RadioRenderingMode;
		'radio-stylized': RadioStylizedRenderingMode;
	} = <any>{};

	static override initialize() {
		InputComponent.initialize.call(this);
		this.standardRenderingModes.dropdown = new DropdownRenderingMode;
		this.standardRenderingModes.radio = new RadioRenderingMode;
		this.standardRenderingModes['radio-stylized'] = new RadioStylizedRenderingMode;
	};

	component_DOM: HTMLDivElement;
	renderingMode!: TInputSelectRenderingMode<TComponent, TConfiguration>;

	async reset() {
		const dv = this.component_configuration.defaultValue;
		if (typeof dv === 'string') {
			await this.updateValueFromId(dv);
		} else {
			await this.updateValue(dv || null);
		}
	};

	override isValueTheDefault() {
		return (this.value === this.defaultValue);
	};

	protected async onInputButton(ev: STD.events.Event<any, CM.Component<any, any>>) {
		ev.stopImmediatePropagation();
		ev.stopPropagation();
		await this.updateValue(ev.target as CM.Component<any, any>);
	};

	protected async onValueUpdated(ev: STD.events.Event<any, CM.Component<any, any>>) {
		await <any>void 1;
		const oldValue = <CM.DOMComponent<any, any, any> | null>ev.details.oldValue;
		const newValue = <CM.DOMComponent<any, any, any> | null>ev.details.newValue;
		if (oldValue && oldValue.component_DOM) {
			oldValue.component_DOM.classList.remove('selected');
		}
		if (newValue && newValue.component_DOM) {
			newValue.component_DOM.classList.add('selected');
		}
	};

	override async component_appendOuterChild(component: InputButtonComponent) {
		const slot = this.renderingMode.getSlotDOM(this);
		await super.component_appendOuterChild(component, slot);
	};

	override async component_appendInnerChild(component: InputButtonComponent) {
		const slot = this.renderingMode.getSlotDOM(this);
		await super.component_appendOuterChild(component, slot);
	};

	refreshFromCurrentValue() {
		if (this.value) {
			(<CM.DOMComponent<HTMLDivElement, any, any>>this.value).component_DOM.classList.add('selected');
		}
	};

	override serialize() {
		return this.value ? this.value.id : null;
	};

	override deserialize(
		optionComponentOrId: CM.Component<any, any> | CM.Component.TId
	) {
		if (!optionComponentOrId) {
			return (null);
		}
		if (optionComponentOrId instanceof CM.Component) {
			return (optionComponentOrId);
		}
		let c = this.component_findOuter([optionComponentOrId]);
		if (!c) {
			if (this.component_parent &&
				(this.component_tree !== this.component_parent) &&
				(this.component_tree === this.component_parent.component_tree)
			) {
				c = this.component_findInner([optionComponentOrId]);
			}
			if (!c) {
				this.component_throwLibraryError(`Option<${optionComponentOrId}> not found!`, {optionComponentOrId});
			}
		}
		return (c);
	};

	async updateValueFromId(id: string) {
		if (id[0] !== '@') {
			id = '@' + id;
		}
		const child = this.component_find(id);
		if (!child) {
			throw STD.errors.user(`Child<${id}> not found!`, {
				component: this,
				id
			});
		}
		await this.updateValue(child);
	};

	override async component_finalize() {
		if (this.component_configuration.renderingMode && (typeof this.component_configuration.renderingMode !== 'string')) {
			const getRM = CM.PropertyOperation.create(
				this.component_configuration.renderingMode
			);
			const RM = await getRM.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (!(RM instanceof CM.Plugin)) {
				this.component_throwUserError('Invalid rendering mode', {
					renderingModePropertyPathValueGetter: getRM
				});
			}
			this.renderingMode = <any>RM;
		} else {
			this.renderingMode =
				<any>InputSelectComponent.standardRenderingModes[this.component_configuration.renderingMode ?? 'dropdown'];
		}
		await super.component_finalize();
		this.renderingMode.attach(this);
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: TConfiguration
	) {
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
		this.addEventListener('value', this.onInputButton.bind(this), {
			subType: 'input-button',
			listener: this,
			disableEventExistsCheck: true
		});
		this.addEventListener('value', this.onValueUpdated.bind(this), {
			subType: 'input-select',
			listener: this
		});
	};
};
export namespace NS {
	export const Component = InputSelectComponent;
	export type Component = InputSelectComponent<any>;
	export type Configuration = IInputSelectComponentConfiguration;
	export type RenderingMode = TInputSelectRenderingMode<any>;
}
;
import './InputSelect.scss';
import {
	InputComponent,
	IInputComponentConfiguration
}                                     from '../Input/Input';
import { DropdownRenderingMode }      from './renderingModes/Dropdown';
import { RadioRenderingMode }         from './renderingModes/Radio';
import { RadioStylizedRenderingMode } from './renderingModes/RadioStylized';
import { InputButtonComponent }       from '../InputButton/InputButton';

CM.Manager.declare({
	id: 'input-select',
	type: InputSelectComponent
});
